import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Alert, Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { ApiError, errorMessage, toApiError } from '@/api/client';

function StateBox({ icon, title, description, action }: { icon: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 8, px: 2, textAlign: 'center', color: 'text.secondary' }} role="status">
      <Box sx={{ fontSize: 44, display: 'flex', color: 'text.disabled' }}>{icon}</Box>
      <Typography variant="h5" color="text.primary">
        {title}
      </Typography>
      {description && <Typography variant="body2" sx={{ maxWidth: 480 }}>{description}</Typography>}
      {action}
    </Stack>
  );
}

export function EmptyState({ title = 'Nothing here yet', description, action }: { title?: string; description?: string; action?: ReactNode }) {
  return <StateBox icon={<InboxOutlinedIcon fontSize="inherit" />} title={title} description={description} action={action} />;
}

export function ForbiddenState({ description }: { description?: string }) {
  return (
    <StateBox
      icon={<LockOutlinedIcon fontSize="inherit" />}
      title="You don’t have access to this"
      description={description ?? 'Your role does not include permission for this page or record. Contact your administrator if you need access.'}
      action={<Button component={RouterLink} to="/app/dashboard">Back to dashboard</Button>}
    />
  );
}

export function NotFoundState({ what = 'page' }: { what?: string }) {
  return <StateBox icon={<SearchOffIcon fontSize="inherit" />} title={`This ${what} was not found`} description="It may have been removed, or it belongs to another organization." action={<Button component={RouterLink} to="/app/dashboard">Back to dashboard</Button>} />;
}

/** Maps API errors to the right state: forbidden, not found or a generic retryable error. */
export function ErrorState({ error, onRetry, what }: { error: unknown; onRetry?: () => void; what?: string }) {
  const e = toApiError(error);
  if (e.status === 403) return <ForbiddenState description={e.message} />;
  if (e.status === 404) return <NotFoundState what={what} />;
  return (
    <StateBox
      icon={<ErrorOutlineIcon fontSize="inherit" />}
      title="Something went wrong"
      description={`${errorMessage(e)}${e.requestId ? ` (request ${e.requestId.slice(0, 8)})` : ''}`}
      action={onRetry && <Button onClick={onRetry}>Try again</Button>}
    />
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : toApiError(error);
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      {errorMessage(e)}
    </Alert>
  );
}

export function PageSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <Stack spacing={1.5} aria-busy="true" aria-label="Loading">
      <Skeleton variant="rounded" height={36} width="40%" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={28} />
      ))}
    </Stack>
  );
}

export function FullPageLoader() {
  return (
    <Box sx={{ height: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress aria-label="Loading" />
    </Box>
  );
}
